/* eslint-disable import/prefer-default-export */
import { useMemo, useEffect } from 'react';
import { useAppDispatch } from 'redux/store';
import { usePaginatedApi } from 'shared/hooks/use-paginated-api';
import { getMentoringProgramEnrollments } from 'redux/actions/mentoring-program-enrollments';
import { MentoringProgramRole, MentoringProgramEnrollment } from 'redux/schemas/models/mentoring-program-enrollments';
import { getMentoringProgramEnrollmentsList } from 'redux/selectors/mentorship-program-enrollments';

type UseMentoringProgramAdminsParams = {
  programId: number;
};

export const useMentoringProgramAdmins = ({ programId }: UseMentoringProgramAdminsParams) => {
  const dispatch = useAppDispatch();

  const params = useMemo(
    () => ({
      pageSize: 100,
      programId,
      courseRoleName: MentoringProgramRole.MENTORSHIP_PROGRAM_ADMIN,
    }),
    [programId],
  );

  const { result: retrievedAdmins, reset, loadMore, isLoading, allLoaded } = usePaginatedApi<
  MentoringProgramEnrollment,
  { programId: number; pageSize: number; courseRoleName: MentoringProgramRole }
  >(
    (p) => (p.programId
      ? dispatch(
        getMentoringProgramEnrollments({
          ...p,
        }),
      ).then((action) => action.payload as MentoringProgramEnrollment[])
      : undefined),
    params,
    getMentoringProgramEnrollmentsList,
  );

  // Automatically fetch all pages of admins to ensure complete client-side search/selection
  useEffect(() => {
    if (!allLoaded && !isLoading && retrievedAdmins !== null) {
      loadMore();
    }
  }, [allLoaded, isLoading, retrievedAdmins, loadMore]);

  return {
    reset,
    loadMore,
    isLoading,
    allLoaded,
    admins: retrievedAdmins,
  };
};
